import { useState } from "react";
import { useUserAccounts } from "@trader-dashboard/shared";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReportsApi, TaxReportData } from '@/api/ReportsApi';
import { format } from 'date-fns';
import { ArrowDownLeft, ArrowUpRight, Download } from 'lucide-react';
import { formatCurrency } from '@/utils/formatCurrency';
import { generateTaxReportPDF } from '@/utils/generateTaxReportPDF';

export const YearlyTaxReportPage = (): React.ReactNode => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [reportData, setReportData] = useState<TaxReportData | undefined>(undefined);

  const { selectedAccountId } = useUserAccounts();

  // Generate year options (current year and 5 years back)
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const loadReport = async () => {
    if (!selectedAccountId || !selectedYear) return;

    setLoading(true);
    setError(undefined);

    try {
      const data = await ReportsApi.getTaxReport(
        selectedAccountId,
        parseInt(selectedYear),
        'Australia/Sydney'
      );
      setReportData(data);
    } catch (err: any) {
      console.error('Error loading tax report:', err);
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    if (!reportData) return;
    generateTaxReportPDF(reportData);
  };

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Yearly Tax Report</CardTitle>
          <CardDescription>
            Comprehensive tax report including trading performance and fund movements
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Select Year</label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={loadReport} disabled={loading}>
              {loading ? 'Loading...' : 'Generate Report'}
            </Button>
            {reportData && (
              <Button onClick={handleExportPDF} variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                Export to PDF
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card>
          <CardContent className="p-8">
            <div className="text-center text-red-600">Error: {error}</div>
          </CardContent>
        </Card>
      )}

      {reportData && (
        <>
          {/* Broker Information */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="text-2xl font-bold">{reportData.broker.name}</div>
              </div>
            </CardContent>
          </Card>

          {/* Profit Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Trading Performance - Year {reportData.year}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Net Profit</div>
                  <div className={`text-3xl font-bold ${reportData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(reportData.netProfit)}
                  </div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Gross Profit</div>
                  <div className={`text-3xl font-bold ${reportData.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(reportData.grossProfit)}
                  </div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Commission</div>
                  <div className={`text-3xl font-bold ${reportData.commission >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(reportData.commission)}
                  </div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Swap</div>
                  <div className={`text-3xl font-bold ${reportData.swap >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(reportData.swap)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fund Transactions */}
          <Card>
            <CardHeader>
              <CardTitle>Fund Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {reportData.fundTransactions.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  No fund transactions in {reportData.year}
                </div>
              ) : (
                <div className="space-y-2">
                  {reportData.fundTransactions.map((tx, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {tx.type === 'deposit' ? (
                          <ArrowDownLeft className="h-5 w-5 text-green-600" />
                        ) : (
                          <ArrowUpRight className="h-5 w-5 text-red-600" />
                        )}
                        <div>
                          <div className="font-medium">
                            {tx.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {format(new Date(tx.date), 'MMM dd, yyyy')}
                          </div>
                        </div>
                      </div>
                      <div className={`text-lg font-semibold ${tx.type === 'deposit' ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(tx.type === 'deposit' ? tx.amount : -tx.amount, { showSign: true })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* End of Year Balance */}
          <Card>
            <CardHeader>
              <CardTitle>End of Year Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border p-6 bg-muted/50">
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  Account Balance as of December 31, {reportData.year}
                </div>
                <div className={`text-4xl font-bold ${reportData.endOfYearBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(reportData.endOfYearBalance)}
                </div>
                <div className="text-xs text-muted-foreground mt-3">
                  Calculated as: Total Deposits - Total Withdrawals + Net Trading Profit
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
